import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { PrinterLibraryQuery, PrinterWithRelations } from '@maker-toolchest/shared';
import { SearchInput } from '@/components/SearchInput';
import { Select } from '@/components/Select';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/Button';
import { Pagination } from '@/components/Pagination';
import { FilterBar } from '@/components/FilterBar';
import { Plus, ExternalLink, Eye } from 'lucide-react';
import {
  usePrinterLibrary,
  usePrinterBrands,
} from '../hooks';

interface PrinterLibraryBrowserProps {
  onPrinterSelect?: (printerId: string) => void;
}

export function PrinterLibraryBrowser({ onPrinterSelect }: PrinterLibraryBrowserProps) {
  const [filters, setFilters] = useState<PrinterLibraryQuery>({
    page: 1,
    limit: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    includeSystem: true,
    includeCustom: false,  // Only show system printers in library
  });
  const [searchKey, setSearchKey] = useState(0);

  const { data, isLoading } = usePrinterLibrary(filters);
  const { data: brands = [] } = usePrinterBrands();

  const printers = data?.items || [];
  const pagination = data?.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 };
  const isInitialLoad = isLoading && !data;

  const handleSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }));
  }, []);

  const handleFilterChange = useCallback((key: keyof PrinterLibraryQuery, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 25,
      sortBy: 'name',
      sortOrder: 'asc',
      includeSystem: true,
      includeCustom: false,
    });
  }, []);

  const handleApplyFilters = useCallback((newFilters: Record<string, any>) => {
    // Set all filters including search
    setFilters(prev => ({
      ...prev,
      search: newFilters.search || undefined,
      brandId: newFilters.brandId || undefined,
      technology: newFilters.technology || undefined,
      page: 1,
    }));

    // Force remount of SearchInput to clear stale debounced state
    setSearchKey(prev => prev + 1);
  }, []);

  const hasActiveFilters = !!(
    filters.search ||
    filters.brandId ||
    filters.technology
  );

  // Build options for selects (memoized to prevent re-renders)
  const brandOptions = useMemo(() => [
    { value: '', label: 'All Brands' },
    ...brands.map((brand) => ({ value: brand.id, label: brand.name })),
  ], [brands]);

  const technologyOptions = useMemo(() => [
    { value: '', label: 'All Technologies' },
    { value: 'FDM', label: 'FDM' },
    { value: 'SLA', label: 'SLA' },
    { value: 'SLS', label: 'SLS' },
    { value: 'MJF', label: 'MJF' },
    { value: 'BINDER_JET', label: 'Binder Jet' },
  ], []);

  // Only show full-page spinner on initial load
  if (isInitialLoad) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <FilterBar
        searchField={
          <SearchInput
            key={searchKey}
            value={filters.search || ''}
            onChange={handleSearch}
            placeholder="Search printers..."
          />
        }
        filterFields={[
          <div className="w-40">
            <Select
              label="Brand"
              value={filters.brandId || ''}
              onChange={(e) => handleFilterChange('brandId', e.target.value)}
              options={brandOptions}
            />
          </div>,
          <div className="w-40">
            <Select
              label="Technology"
              value={filters.technology || ''}
              onChange={(e) => handleFilterChange('technology', e.target.value)}
              options={technologyOptions}
            />
          </div>,
        ]}
        filterConfigs={[
          { key: 'brandId', label: 'Brand', type: 'select', options: brandOptions },
          { key: 'technology', label: 'Technology', type: 'select', options: technologyOptions },
        ]}
        filterValues={{
          search: filters.search || '',
          brandId: filters.brandId || '',
          technology: filters.technology || '',
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Results */}
      <div className="relative">
        {/* Loading overlay for subsequent loads */}
        {isLoading && data && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
            <Spinner size="md" />
          </div>
        )}

        {printers.length === 0 ? (
          <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
            <p className="text-foreground-muted">No printers found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background-subtle">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Printer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Build Volume
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Technology
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-foreground-muted">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-background">
              {printers.map((printer: PrinterWithRelations) => (
                <tr key={printer.id} className="hover:bg-background-subtle/50">
                  <td className="whitespace-nowrap px-4 py-3">
                    <div>
                      <Link
                        to={`/printers/library/${printer.id}`}
                        className="group inline-flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary"
                      >
                        {printer.name}
                        <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                      </Link>
                      <div className="text-xs text-foreground-muted">
                        {printer.brand?.name} - {printer.model?.name}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                    {printer.buildVolumeX && printer.buildVolumeY && printer.buildVolumeZ
                      ? `${printer.buildVolumeX}×${printer.buildVolumeY}×${printer.buildVolumeZ}mm`
                      : '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                    {printer.technology || '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/printers/library/${printer.id}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onPrinterSelect?.(printer.id)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Printer
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Pagination */}
      {printers.length > 0 && (
        <Pagination
          page={pagination.page}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}
